import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LayoutService } from '../service/layout.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  standalone: true,
  imports: [CommonModule, RouterModule, RippleModule],
  template: `
    <ng-container>
      @if (root && item.visible !== false && item.label) {
        <div class="layout-menuitem-root-text">{{ item.label }}</div>
      }

      @if ((!item.routerLink || hasChildren()) && item.visible !== false) {
        <a
          [attr.href]="item.url"
          (click)="itemClick($event)"
          [ngClass]="item.styleClass || item['class']"
          [class.active-route]="hasActiveChild()"
          [attr.target]="item.target"
          tabindex="0"
          pRipple
        >
          <i class="layout-menuitem-icon" [ngClass]="item.icon || 'pi pi-circle'" aria-hidden="true"></i>
          <span class="layout-menuitem-text">{{ item.label }}</span>

          @if (hasChildren() && shouldShowToggler()) {
            <i
              class="layout-submenu-toggler pi pi-fw"
              [ngClass]="isActive() ? 'pi-angle-up' : 'pi-angle-down'"
              aria-hidden="true"
            ></i>
          }
        </a>
      }

      @if (item.routerLink && !hasChildren() && item.visible !== false) {
        <a
          (click)="itemClick($event)"
          [ngClass]="item.styleClass || item['class']"
          [class.active-route]="hasActiveChild()"
          [routerLink]="item.routerLink"
          routerLinkActive="active-route"
          [routerLinkActiveOptions]="
            item.routerLinkActiveOptions || {
              paths: 'exact',
              queryParams: 'ignored',
              matrixParams: 'ignored',
              fragment: 'ignored'
            }
          "
          [fragment]="item.fragment"
          [queryParamsHandling]="item.queryParamsHandling"
          [preserveFragment]="item.preserveFragment"
          [skipLocationChange]="item.skipLocationChange"
          [replaceUrl]="item.replaceUrl"
          [state]="item.state"
          [queryParams]="item.queryParams"
          [attr.target]="item.target"
          tabindex="0"
          pRipple
        >
          <i class="layout-menuitem-icon" [ngClass]="item.icon || 'pi pi-circle'" aria-hidden="true"></i>
          <span class="layout-menuitem-text">{{ item.label }}</span>
        </a>
      }

      @if (hasChildren() && item.visible !== false && (root || isActive())) {
        <ul class="layout-submenu">
          @for (child of item.items; track child; let i = $index) {
            <li
              app-menuitem
              [item]="child"
              [index]="i"
              [parentPath]="selfPath"
              [root]="false"
              [class]="child['badgeClass']"
              [class.parent-submenu-active]="isChildRouteActive(child)"
            ></li>
          }
        </ul>
      }
    </ng-container>
  `,
  host: {
    '[class.active-menuitem]': 'isActive()',
    '[class.layout-root-menuitem]': 'root',
    '[class.parent-submenu-active]': 'hasActiveChild()'
  }
})
export class AppMenuitem implements OnInit, OnDestroy {
  @Input() item!: MenuItem;
  @Input() index!: number;
  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;
  @Input() parentPath?: string | null;

  selfPath = '';

  private routerEventsSubscription?: Subscription;

  constructor(
    public router: Router,
    private layoutService: LayoutService
  ) {}

  ngOnInit() {
    const parent = this.parentPath ?? 'root';
    this.selfPath = `${parent}/${this.index}`;

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if ((this.item as any)?.routerLink) {
          this.updateActiveStateFromRoute();
        }
      });

    if ((this.item as any)?.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  ngOnDestroy() {
    this.routerEventsSubscription?.unsubscribe();
  }

  hasChildren(): boolean {
    return Array.isArray((this.item as any)?.items) && (this.item as any).items.length > 0;
  }

  isActive(): boolean {
    const state: any = this.layoutService.layoutState();
    const activePath: string = state?.activePath || '';
    return activePath === this.selfPath || activePath.startsWith(this.selfPath + '/');
  }

  shouldShowToggler(): boolean {
    const s = this.layoutService.layoutState();
    return !!(s.overlayMenuActive || s.staticMenuMobileActive);
  }

  private updateActiveStateFromRoute() {
    const itemAny: any = this.item as any;
    if (!itemAny?.routerLink) {
      return;
    }

    const link = Array.isArray(itemAny.routerLink) ? itemAny.routerLink[0] : itemAny.routerLink;

    const isRouteActive = this.router.isActive(link, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    });

    if (isRouteActive) {
      const parent = this.parentPath ?? null;
      if (parent) {
        this.layoutService.layoutState.update((val) => ({
          ...val,
          activePath: parent
        }));
      }
    }
  }

  isChildRouteActive(child: MenuItem): boolean {
    const childAny: any = child as any;
    if (!childAny?.routerLink) {
      return false;
    }

    const link = Array.isArray(childAny.routerLink) ? childAny.routerLink[0] : childAny.routerLink;

    return this.router.isActive(link, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    });
  }

  hasActiveChild(): boolean {
    const children = (this.item as any)?.items;
    if (!Array.isArray(children) || !children.length) {
      return false;
    }

    return children.some((child: MenuItem) => this.isChildRouteActive(child));
  }

  itemClick(event: Event) {
    const itemAny: any = this.item as any;

    if (itemAny?.disabled) {
      event.preventDefault();
      return;
    }

    itemAny?.command?.({ originalEvent: event, item: itemAny });

    if (this.hasChildren()) {
      event.preventDefault();

      if (this.isActive()) {
        this.layoutService.layoutState.update((val) => ({
          ...val,
          activePath: this.parentPath ?? ''
        }));
      } else {
        this.layoutService.layoutState.update((val) => ({
          ...val,
          activePath: this.selfPath,
          menuHoverActive: true,
          overlayMenuActive: true,
          staticMenuMobileActive: true
        }));
      }
      return;
    }

    this.layoutService.layoutState.update((val) => ({
      ...val,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      mobileMenuActive: false,
      menuHoverActive: false,
      activePath: ''
    }));
  }
}